import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { useViewMode } from '../contexts/ViewModeContext';
import { formatDate, formatDateTime } from '../lib/dateUtils';
import { 
  MessageSquare, Send, Paperclip, X, Filter, Users, Building,
  CheckCircle, Clock, AlertCircle, Loader2, ArrowLeft, Image,
  User, Briefcase, Trash2
} from 'lucide-react';
import { HeaderSkeleton, TableSkeleton, SkeletonBar } from './skeletons/Skeletons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminHelpDeskPage() {
  const { isMobileView } = useViewMode();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    department: '',
    creator_type: '',
    status: ''
  });
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchDepartments();
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [filters]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedTicket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchTickets = async () => {
    try {
      const params = {};
      if (filters.department) params.department = filters.department;
      if (filters.creator_type) params.creator_type = filters.creator_type;
      if (filters.status) params.status = filters.status;
      
      const res = await adminApi.get('/api/admin/helpdesk/tickets', { params });
      setTickets(res.data || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await adminApi.get('/api/admin/helpdesk/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await adminApi.get('/api/admin/departments');
      setDepartments(res.data || []);
    } catch (error) {
      console.error('Failed to load departments');
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await adminApi.get('/api/admin/employees');
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Failed to load employees');
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const res = await adminApi.get(`/api/admin/helpdesk/tickets/${ticketId}`);
      setSelectedTicket(res.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await adminApi.post('/api/files/upload-s3', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data?.url) {
          uploadedUrls.push(response.data.url);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploadedUrls.length > 0) {
      await sendReply(newMessage || 'Attached file(s)', uploadedUrls);
    }

    setUploading(false);
    e.target.value = '';
  };

  const sendReply = async (message, attachments = []) => {
    if (!message.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      await adminApi.post(`/api/admin/helpdesk/tickets/${selectedTicket._id}/reply`, {
        message: message.trim(),
        attachments
      });
      setNewMessage('');
      fetchTicketDetails(selectedTicket._id);
      fetchTickets();
      toast.success('Reply sent');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    sendReply(newMessage);
  };

  const handleStatusChange = async (status) => {
    try {
      await adminApi.put(`/api/admin/helpdesk/tickets/${selectedTicket._id}/status`, { status });
      toast.success('Status updated');
      fetchTickets();
      fetchTicketDetails(selectedTicket._id);
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;
    
    setDeleting(true);
    try {
      await adminApi.delete(`/api/admin/helpdesk/tickets/${ticketToDelete._id}`);
      toast.success('Ticket deleted successfully');
      setTicketToDelete(null);
      setSelectedTicket(null);
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete ticket');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-neutral-100 text-[#1a1a2e] border-white/40'
    };
    return styles[status] || styles.open;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-neutral-100 text-neutral-600',
      medium: 'bg-orange-100 text-orange-600',
      high: 'bg-red-100 text-red-600'
    };
    return styles[priority] || styles.medium;
  };

  const getCreatorTypeStyle = (type) => {
    return type === 'client' 
      ? 'border-l-4 border-l-blue-500' 
      : 'border-l-4 border-l-purple-500';
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="admin-help-desk-loading">
        <HeaderSkeleton />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="soft-card p-5 space-y-3">
              <SkeletonBar className="h-6 w-6 rounded" />
              <SkeletonBar className="h-3 w-24" />
              <SkeletonBar className="h-7 w-12 bg-neutral-300" />
            </div>
          ))}
        </div>
        <TableSkeleton rows={5} columns={5} />
      </div>
    );
  }

  // Ticket Detail View (Chat Interface)
  if (selectedTicket) {
    return (
      <div className="h-[calc(100vh-80px)] flex flex-col bg-white/40">
        {/* Header */}
        <div className="bg-white border-b border-white/40 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSelectedTicket(null)}
                className="p-2 hover:bg-neutral-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-neutral-600" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-[#1a1a2e]">{selectedTicket.subject}</h2>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    selectedTicket.creator_type === 'client' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {selectedTicket.creator_type === 'client' ? (
                      <><Briefcase className="w-3 h-3 inline mr-1" />Client</>
                    ) : (
                      <><User className="w-3 h-3 inline mr-1" />Employee</>
                    )}
                  </span>
                </div>
                <p className="text-sm text-neutral-500 mt-1">
                  From: {selectedTicket.creator_name} • {selectedTicket.department}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedTicket.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className={`px-4 py-2 rounded-xl border font-medium ${getStatusBadge(selectedTicket.status)}`}
                data-testid="ticket-status-select"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <button
                onClick={() => setTicketToDelete(selectedTicket)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                title="Delete Ticket"
                data-testid="delete-ticket-btn"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Initial Ticket */}
          <div className={`bg-white rounded-xl p-4 shadow-soft border max-w-3xl ${
            selectedTicket.creator_type === 'client' ? 'border-blue-200' : 'border-purple-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                selectedTicket.creator_type === 'client' ? 'bg-blue-100' : 'bg-purple-100'
              }`}>
                <span className={`font-medium text-sm ${
                  selectedTicket.creator_type === 'client' ? 'text-blue-600' : 'text-purple-600'
                }`}>
                  {selectedTicket.creator_name?.charAt(0) || 'U'}
                </span>
              </div>
              <div>
                <p className="font-medium text-[#1a1a2e] text-sm">
                  {selectedTicket.creator_name}
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                    selectedTicket.creator_type === 'client' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                  }`}>
                    {selectedTicket.creator_type}
                  </span>
                </p>
                <p className="text-xs text-neutral-500">
                  {formatDateTime(selectedTicket.created_at)}
                </p>
              </div>
            </div>
            <p className="text-neutral-700 whitespace-pre-wrap">{selectedTicket.description}</p>
            {selectedTicket.attachments?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedTicket.attachments.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-100 rounded-xl text-sm text-blue-600 hover:bg-neutral-200"
                  >
                    <Paperclip className="w-3 h-3" />
                    Attachment {idx + 1}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Messages */}
          {selectedTicket.messages?.map((msg, index) => (
            <div
              key={index}
              className={`max-w-3xl ${msg.sender_type === 'admin' ? 'ml-auto' : ''}`}
            >
              <div className={`rounded-xl p-4 shadow-soft border ${
                msg.sender_type === 'admin' 
                  ? 'bg-pink-50 border-pink-200' 
                  : msg.sender_type === 'client'
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-purple-50 border-purple-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    msg.sender_type === 'admin' ? 'bg-pink-200' :
                    msg.sender_type === 'client' ? 'bg-blue-200' : 'bg-purple-200'
                  }`}>
                    <span className={`font-medium text-sm ${
                      msg.sender_type === 'admin' ? 'text-pink-600' :
                      msg.sender_type === 'client' ? 'text-blue-600' : 'text-purple-600'
                    }`}>
                      {msg.sender_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a2e] text-sm">
                      {msg.sender_name}
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                        msg.sender_type === 'admin' ? 'bg-pink-100 text-pink-600' :
                        msg.sender_type === 'client' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                      }`}>
                        {msg.sender_type}
                      </span>
                    </p>
                    <p className="text-xs text-neutral-500">
                      {formatDateTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
                <p className="text-neutral-700 whitespace-pre-wrap">{msg.message}</p>
                {msg.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {msg.attachments.map((url, idx) => (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1 bg-white rounded-xl text-sm text-blue-600 hover:bg-white/60"
                      >
                        <Paperclip className="w-3 h-3" />
                        Attachment {idx + 1}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Input */}
        <div className="bg-white border-t border-white/40 p-4">
          <form onSubmit={handleSendMessage} className="flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your reply..."
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 resize-none"
                rows={2}
                spellCheck={true}
                autoCorrect="on"
                autoCapitalize="sentences"
              />
            </div>
            <input
              type="file"
              id="admin-chat-file-upload"
              className="hidden"
              multiple
              onChange={handleFileUpload}
              accept="image/*,.pdf,.doc,.docx"
            />
            <label
              htmlFor="admin-chat-file-upload"
              className={`p-3 rounded-xl border border-white/40 hover:bg-neutral-100 cursor-pointer transition-colors ${uploading ? 'opacity-50' : ''}`}
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
              ) : (
                <Image className="w-5 h-5 text-neutral-500" />
              )}
            </label>
            <button
              type="submit"
              disabled={sending || (!newMessage.trim())}
              className="p-3 bg-pink-500 text-white rounded-xl hover:bg-pink-600 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={isMobileView ? 'px-4 py-4' : 'p-8'}>
      {/* Header */}
      <div className={isMobileView ? 'mb-4' : 'mb-8'}>
        <h1 className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-3xl'}`}>Help Desk</h1>
        <p className={`text-neutral-600 ${isMobileView ? 'text-sm' : 'mt-1'}`}>Manage support tickets</p>
      </div>

      {/* Stats Cards - Mobile responsive */}
      <div className={`grid gap-3 ${isMobileView ? 'grid-cols-2 mb-4' : 'grid-cols-5 gap-4 mb-8'}`}>
        <div className={`glass-card ${isMobileView ? 'p-3' : 'p-4'}`}>
          <p className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-2xl'}`}>{stats?.total || 0}</p>
          <p className={`text-neutral-600 ${isMobileView ? 'text-xs' : 'text-sm'}`}>Total Tickets</p>
        </div>
        <div className={`bg-yellow-50 rounded-xl border border-yellow-200 ${isMobileView ? 'p-3' : 'p-4'}`}>
          <p className={`font-bold text-yellow-600 ${isMobileView ? 'text-xl' : 'text-2xl'}`}>{stats?.open || 0}</p>
          <p className={`text-yellow-700 ${isMobileView ? 'text-xs' : 'text-sm'}`}>Open</p>
        </div>
        <div className={`bg-blue-50 rounded-xl border border-blue-200 ${isMobileView ? 'p-3' : 'p-4'}`}>
          <p className={`font-bold text-blue-600 ${isMobileView ? 'text-xl' : 'text-2xl'}`}>{stats?.in_progress || 0}</p>
          <p className={`text-blue-700 ${isMobileView ? 'text-xs' : 'text-sm'}`}>In Progress</p>
        </div>
        <div className={`bg-green-50 rounded-xl border border-green-200 ${isMobileView ? 'p-3' : 'p-4'}`}>
          <p className={`font-bold text-green-600 ${isMobileView ? 'text-xl' : 'text-2xl'}`}>{stats?.resolved || 0}</p>
          <p className={`text-green-700 ${isMobileView ? 'text-xs' : 'text-sm'}`}>Resolved</p>
        </div>
        {!isMobileView && (
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <div className="flex gap-4">
              <div>
                <p className="text-lg font-bold text-purple-600">{stats?.by_type?.employee || 0}</p>
                <p className="text-xs text-purple-700">Employees</p>
              </div>
              <div>
                <p className="text-lg font-bold text-blue-600">{stats?.by_type?.client || 0}</p>
                <p className="text-xs text-blue-700">Clients</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters - Mobile responsive */}
      <div className={`glass-card mb-4 ${isMobileView ? 'p-3' : 'p-4 mb-6'}`}>
        <div className={`flex items-center gap-2 ${isMobileView ? 'flex-wrap' : 'gap-4'}`}>
          <Filter className={`text-neutral-500 ${isMobileView ? 'w-4 h-4' : 'w-5 h-5'}`} />
          <select
            value={filters.department}
            onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
            className={`border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${isMobileView ? 'flex-1 px-2 py-1.5 text-sm min-w-[100px]' : 'px-4 py-2'}`}
          >
            <option value="">All Depts</option>
            {departments.map(dept => (
              <option key={dept._id} value={dept.name}>{dept.name}</option>
            ))}
            <option value="Client Support">Client Support</option>
          </select>
          <select
            value={filters.creator_type}
            onChange={(e) => setFilters(prev => ({ ...prev, creator_type: e.target.value }))}
            className={`border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${isMobileView ? 'flex-1 px-2 py-1.5 text-sm min-w-[80px]' : 'px-4 py-2'}`}
          >
            <option value="">All Types</option>
            <option value="employee">Employee</option>
            <option value="client">Client</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className={`border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${isMobileView ? 'flex-1 px-2 py-1.5 text-sm min-w-[80px]' : 'px-4 py-2'}`}
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          {(filters.department || filters.creator_type || filters.status) && (
            <button
              onClick={() => setFilters({ department: '', creator_type: '', status: '' })}
              className="text-sm text-pink-600 hover:text-pink-700 whitespace-nowrap"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Tickets List - Mobile responsive */}
      {tickets.length === 0 ? (
        <div className={`glass-card text-center ${isMobileView ? 'p-8' : 'p-12'}`}>
          <MessageSquare className={`mx-auto text-neutral-300 mb-4 ${isMobileView ? 'w-12 h-12' : 'w-16 h-16'}`} />
          <h3 className={`font-semibold text-neutral-700 mb-2 ${isMobileView ? 'text-lg' : 'text-xl'}`}>No tickets found</h3>
          <p className="text-neutral-500 text-sm">Tickets will appear here when users create them</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <div
              key={ticket._id}
              className={`glass-card hover:shadow-lg transition-shadow ${getCreatorTypeStyle(ticket.creator_type)} ${isMobileView ? 'p-4' : 'p-6'}`}
            >
              <div className="flex items-start justify-between">
                <div 
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => fetchTicketDetails(ticket._id)}
                >
                  <div className={`flex items-center gap-2 mb-2 ${isMobileView ? 'flex-wrap' : 'gap-3'}`}>
                    <h3 className={`font-semibold text-[#1a1a2e] ${isMobileView ? 'text-sm w-full' : ''}`}>{ticket.subject}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      ticket.creator_type === 'client' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {ticket.creator_type === 'client' ? 'Client' : 'Employee'}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(ticket.status)}`}>
                      {ticket.status?.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-600 line-clamp-2">{ticket.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {ticket.creator_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {ticket.department}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(ticket.created_at)}
                    </span>
                    {ticket.messages?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {ticket.messages.length} replies
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTicketToDelete(ticket);
                    }}
                    className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    title="Delete Ticket"
                    data-testid={`delete-ticket-${ticket._id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    ticket.creator_type === 'client' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                    {ticket.creator_type === 'client' ? (
                      <Briefcase className="w-5 h-5 text-blue-600" />
                    ) : (
                      <User className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!ticketToDelete} onOpenChange={(open) => !open && setTicketToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ticket? This action cannot be undone.
              <br />
              <strong className="text-neutral-700">&quot;{ticketToDelete?.subject}&quot;</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTicket}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
